/**
 * refineChunking — split a long formatted transcript into windows for the
 * Dutch→Afrikaans refine pass.
 *
 * Architecture ("transcribe once, correct in post"): AssemblyAI transcribes the
 * full 50–180 min meeting (length + global diarisation + timestamps), then Gemini
 * refines the TEXT. Unlike audio chunking, splitting the text is boundary-safe:
 * speakers and timestamps are already locked by AssemblyAI, and Dutch→Afrikaans
 * correction is local to each segment — so windows need NO overlap and NO
 * cross-window reconciliation. Each window is small enough to avoid the refine
 * endpoint's truncation and stay within Gemini's 45s budget.
 *
 * Pure + deterministic — unit-tested in test/refine-chunking.test.ts.
 */

/** Segments per refine window. ~40 segments keeps each call well under the
 *  refine endpoint's token/timeout limits (a 180-min meeting → ~15–25 windows). */
export const REFINE_WINDOW_SEGMENTS = 40;

/**
 * Split a formatted transcript ("[MM:SS] **Speaker:** text" blocks separated by
 * blank lines) into windows of at most `windowSize` segments. Returns the original
 * single-element array when it already fits one window.
 */
export function splitRefineWindows(formatted: string, windowSize = REFINE_WINDOW_SEGMENTS): string[] {
  const segments = formatted.split(/\n\n+/).map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return [];
  if (segments.length <= windowSize) return [segments.join('\n\n')];

  const windows: string[] = [];
  for (let i = 0; i < segments.length; i += windowSize) {
    windows.push(segments.slice(i, i + windowSize).join('\n\n'));
  }
  return windows;
}
